import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { backdropVariants, modalVariants, useMotionVariants } from '../motion/motionTokens'
import { AngledButton } from './broadcast'
import { ModalCloseButton } from './ModalCloseButton'

interface ConfirmDialogProps {
  open: boolean
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  confirmTone?: 'gold' | 'accent' | 'neutral' | 'danger'
  /** Hide the cancel button — turns the dialog into a styled alert (one action) */
  hideCancel?: boolean
  icon?: ReactNode
  onConfirm: () => void
  onCancel: () => void
}

// Styled, on-brand replacement for window.confirm(): a centered modal that
// respects the page theme (gold-accented surface, angled buttons, spring
// reveal). Portalled to <body> so fixed positioning is viewport-relative
// regardless of any transformed ancestor. Esc cancels, Enter confirms,
// backdrop click cancels.
export function ConfirmDialog({
  open, title, message,
  confirmLabel = 'Confirmar', cancelLabel = 'Cancelar', confirmTone = 'gold', hideCancel,
  icon, onConfirm, onCancel,
}: ConfirmDialogProps) {
  const modal = useMotionVariants(modalVariants)
  const backdrop = useMotionVariants(backdropVariants)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
      else if (e.key === 'Enter') onConfirm()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onCancel, onConfirm])

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            variants={backdrop}
            initial="initial"
            animate="enter"
            exit="exit"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 230 }}
          />
          <motion.div
            variants={modal}
            initial="initial"
            animate="enter"
            exit="exit"
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed', top: '50%', left: '50%', x: '-50%', y: '-50%',
              zIndex: 231,
              background: 'var(--bg-surface)',
              border: '1px solid color-mix(in srgb, var(--gold) 35%, transparent)',
              borderRadius: '16px',
              width: 'min(420px, 92vw)',
              padding: '1.4rem 1.5rem',
              boxShadow: 'var(--shadow-lg), inset 0 3px 0 0 var(--gold)',
              textAlign: 'left',
            }}
          >
            <ModalCloseButton onClick={onCancel} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: message ? '0.65rem' : '1.1rem' }}>
              {icon && <span style={{ color: 'var(--gold)', display: 'flex', flexShrink: 0 }}>{icon}</span>}
              <h3 style={{
                fontFamily: 'var(--font-titles)', fontWeight: 800, fontSize: '1.15rem',
                color: 'var(--text-strong)', margin: 0,
              }}>
                {title}
              </h3>
            </div>

            {message && (
              <div style={{ fontSize: '0.9rem', lineHeight: 1.55, color: 'var(--text-secondary)', marginBottom: '1.3rem' }}>
                {message}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              {!hideCancel && <AngledButton tone="neutral" onClick={onCancel}>{cancelLabel}</AngledButton>}
              <AngledButton tone={confirmTone} onClick={onConfirm}>{confirmLabel}</AngledButton>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}
